import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';

// auth
const Login = React.lazy(() => import('../pages/auth/Login'));
const SignUp = React.lazy(() => import('../pages/auth/SignUp'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const Confirm = React.lazy(() => import('../pages/auth/Confirm'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));

// Projects
const ShikshanAI = React.lazy(() => import('../pages/ShikshanAI'));
const ExamPortal = React.lazy(() => import('../pages/ExamPortal'));
const DLP = React.lazy(() => import('../pages/DigitalLearningProgram'));
const MYCA = React.lazy(() => import('../pages/MYCA'));
const NipunMaharashtra = React.lazy(() => import('../pages/NipunMaharashtra'));
const ProjectsOverview = React.lazy(() => import('../pages/Portfolio'));

//Website Pages
const BlogPost = React.lazy(() => import('../pages/Publications/Blogs/BlogPost'));
const Brouchers = React.lazy(() => import('../pages/Publications/Brouchers'));
const WorkReports = React.lazy(() => import('../pages/ImpactWorkReports'));
const Magazines = React.lazy(() => import('../pages/Publications/Magazines'));
const Blogs = React.lazy(() => import('../pages/Publications/Blogs'));
const FeedbackTestimonials = React.lazy(() => import('../pages/ImpactTestimonialFeedback'));
const MediaMensions = React.lazy(() => import('../pages/NewsAndMedia/MediaMensions'));
const PressReleases = React.lazy(() => import('../pages/NewsAndMedia/PressReleases'));
const Events = React.lazy(() => import('../pages/NewsAndMedia/Events'));
const MediaKit = React.lazy(() => import('../pages/NewsAndMedia/MediaKit'));
const Company = React.lazy(() => import('../pages/Home'));
const About = React.lazy(() => import('../pages/About'));
const Contact = React.lazy(() => import('../pages/Contact'));
const Portfolio = React.lazy(() => import('../pages/Portfolio'));

// CMSpages
const BlogCMS = React.lazy(() => import('../pages/account/Content/Blogs/index'));
const EmployeesCMS = React.lazy(() => import('../pages/account/Content/Employees/index'));
const VacanciesCMS = React.lazy(() => import('../pages/account/Content/Vacancies/index'));
const Dashboard = React.lazy(() => import('../pages/account/Dashboard'));
const Gallery = React.lazy(() => import('../pages/account/Gallery'));
const UserManagement = React.lazy(() => import('../pages/account/UserManagement'));
const Settings = React.lazy(() => import('../pages/account/Settings'));
const WorkReportsCMS = React.lazy(() => import('../pages/account/Content/WorkReports/index'));
const MagzinesCMS = React.lazy(() => import('../pages/account/Content/Magazines/index'));

// Terms and Policies
const NipunMaharashtraTermsAndPolicies = React.lazy(() => import('../pages/TermsAndPolicies'));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
    component: React.LazyExoticComponent<() => JSX.Element>;
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    return useRoutes([
        {
            // root route
            path: '/',
            element: <LoadComponent component={Company} />,
        },
        {
            // root route
            path: '/about',
            element: <LoadComponent component={About} />,
        },

        {
            // root route
            path: '/blog/post/:id',
            element: <LoadComponent component={BlogPost} />,
        },
        {
            // root route
            path: '/portfolio',
            element: <LoadComponent component={Portfolio} />,
        },
        {
            // root route
            path: '/contact',
            element: <LoadComponent component={Contact} />,
        },
        {
            // root route
            path: '/blog',
            element: <LoadComponent component={Magazines} />,
        },

        {
            // public routes
            path: '/',
            children: [
                {
                    path: 'auth',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'signup', element: <LoadComponent component={SignUp} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
                {
                    path: 'publications',
                    children: [
                        { path: 'magazines', element: <LoadComponent component={Magazines} /> },
                        { path: 'blogs', element: <LoadComponent component={Blogs} /> },
                        { path: 'brouchers', element: <LoadComponent component={Brouchers} /> },
                    ],
                },
                {
                    path: 'impact',
                    children: [
                        { path: 'work-reports', element: <LoadComponent component={WorkReports} /> },

                        {
                            path: 'news-and-media',
                            children: [
                                { path: 'media-mentions', element: <LoadComponent component={MediaMensions} /> },
                                { path: 'press-releases', element: <LoadComponent component={PressReleases} /> },
                                { path: 'events', element: <LoadComponent component={Events} /> },
                                { path: 'media-kit', element: <LoadComponent component={MediaKit} /> },
                            ],
                        },

                        {
                            path: 'feedback-and-testimonials',
                            element: <LoadComponent component={FeedbackTestimonials} />,
                        },
                    ],
                },

                {
                    path: 'projects',
                    children: [
                        { path: 'shikshan_ai', element: <LoadComponent component={ShikshanAI} /> },
                        { path: 'nipun-maharashtra', element: <LoadComponent component={NipunMaharashtra} /> },
                        { path: 'digital-learning-project', element: <LoadComponent component={DLP} /> },
                        { path: 'myca', element: <LoadComponent component={MYCA} /> },
                        { path: 'overview', element: <LoadComponent component={Portfolio} /> },
                    ],
                },
                {
                    path: 'services',
                    children: [{ path: 'exam-portal', element: <LoadComponent component={ExamPortal} /> }],
                },
                {
                    path: 'terms-and-policies',
                    children: [
                        {
                            path: 'nipun-maharashtra',
                            element: <LoadComponent component={NipunMaharashtraTermsAndPolicies} />,
                        },
                    ],
                },
            ],
        },
        {
            // protected routes
            path: '/',
            element: <PrivateRoute roles={['Admin', 'User']} />,
            children: [
                {
                    path: 'account',
                    children: [
                        { path: 'dashboard', element: <LoadComponent component={Dashboard} /> },
                        { path: 'userManagement', element: <LoadComponent component={UserManagement} /> },
                        // { path: 'content', element: <LoadComponent component={Content} /> },
                        { path: 'settings', element: <LoadComponent component={Settings} /> },
                        { path: 'gallery', element: <LoadComponent component={Gallery} /> },
                        {
                            path: 'content',
                            children: [
                                { path: 'blogs', element: <LoadComponent component={BlogCMS} /> },
                                { path: 'employees', element: <LoadComponent component={EmployeesCMS} /> },
                                { path: 'vacancies', element: <LoadComponent component={VacanciesCMS} /> },
                                { path: 'work_reports', element: <LoadComponent component={WorkReportsCMS} /> },
                                { path: 'magazines', element: <LoadComponent component={MagzinesCMS} /> },
                            ],
                        },
                    ],
                },
            ],
        },
    ]);
};

export default AllRoutes;
